/**
 * The command stream: connect, de-duplicate, watch for silence.
 *
 * No DOM. `EventSource` arrives as an injected factory ({@link OpenStream}) and time as
 * {@link Timers}, so the whole module tests against fakes in the default `node` environment
 * (REQ-NFR-4).
 *
 * Implements the page half of contract 08 §3 and §6, per design 02 §3.3 and §4.
 */
import { createOutbox, type OutboxStats, type Upstream } from './outbox';
import { eventsUrl, noteUrl, outUrl, relayHost, type RelayBase } from './relay';
import type { Timers } from './timers';

/** What the page did with one command line. Becomes exactly one note (08 §4.3). */
export type RemoteOutcome =
  /** Written to the board. `changed` is present only when a guard rewrote the line. */
  | { kind: 'sent'; changed?: string }
  | { kind: 'fail'; reason: 'noport' | 'write' | 'verb' | 'long' | 'stalled' };

/** The narrow slice of `EventSource` this module uses. `main.ts` implements it over the real one. */
export type StreamSink = {
  onEvent(type: string, id: string, data: string): void;
  onError(): void;
};
export type OpenStream = (url: string, sink: StreamSink) => { close(): void };

/** A `sessionStorage`-shaped thing, injected so tests need no browser. */
export type KeyStore = { get(k: string): string | null; set(k: string, v: string): void };

export type RemoteState = {
  link: 'connecting' | 'linked' | 'silent' | 'stopped';
  /** {@link relayHost} — never the token (REQ-REM-3). */
  host: string;
  /**
   * Milliseconds since the last observable byte from the stream. 02 §3.3 called this "ms in this
   * state", which is ambiguous for `silent`: the banner has to read *"quiet 62s"*, and the number
   * it needs is the silence, not the time since the banner changed. One meaning, and it is the
   * one the read-out uses.
   */
  since: number;
  queued: number;
  dropped: number;
};

export type RemoteClient = {
  start(): void;
  stop(): void;
  /** Called on visibilitychange → visible: timers freeze in a hidden tab (measured, Q7). */
  poke(): void;
  /** Handed to `App` through `Platform` (02 §3.6). */
  upstream: Upstream;
};

/** The server's interval (08 §3.5). It does NOT travel on the wire — see 08 §10.1. */
const KEEPALIVE_MS = 25_000;
/** 3 × the keepalive (08 §6.4). */
const SILENCE_MS = 3 * KEEPALIVE_MS;
/**
 * One missed keepalive is jitter. Two is a problem, and it is two thirds of the way to the
 * rebuild — so the operator watches the link go quiet rather than being surprised by a reconnect.
 */
const QUIET_MS = 2 * KEEPALIVE_MS;
/** Immediate, then backing off to a 30 s ceiling (02 §4.3). */
const RECONNECT_MS = [0, 2_000, 5_000, 15_000, 30_000];
/** Watchdog granularity, and the rate the `since` read-out advances. */
const TICK_MS = 1_000;

type SeenId = { runtag: string; n: number };

/** `<runtag>.<n>` (08 §6.1). Anything else is not ours to interpret. */
function parseId(id: string): SeenId | null {
  const dot = id.lastIndexOf('.');
  if (dot <= 0) return null;
  const runtag = id.slice(0, dot);
  const n = id.slice(dot + 1);
  if (!/^\d+$/.test(n)) return null;
  return { runtag, n: Number(n) };
}

export function createRemote(d: {
  base: RelayBase;
  open: OpenStream;
  post(url: string, body: string): Promise<number>;
  store: KeyStore;
  timers: Timers;
  /** `App.applyRemote` — the one door every remote command goes through (02 §3.5). */
  deliver(line: string): Promise<RemoteOutcome>;
  onState(s: RemoteState): void;
}): RemoteClient {
  const host = relayHost(d.base);
  /**
   * Keyed by relay HOST, not by base: the base contains the token, and a storage key is a place a
   * token gets forgotten. Two tokens on one host can only mean the server restarted, which the
   * runtag rule below already handles.
   */
  const seenKey = `evbweb.relay.${host}.last`;

  let stream: { close(): void } | null = null;
  let link: RemoteState['link'] = 'connecting';
  let lastSignal = 0;
  let attempt = 0;
  let tickHandle: number | null = null;
  let connectHandle: number | null = null;
  let running = false;
  /** Serialises delivery: two concurrent writes would interleave USB bytes and two cfg lines. */
  let chain: Promise<void> = Promise.resolve();
  let seen: SeenId | null = null;
  let stats: OutboxStats = { queued: 0, bytes: 0, dropped: 0, state: 'idle' };

  const outbox = createOutbox({
    post: d.post,
    urls: { out: outUrl(d.base), note: noteUrl(d.base) },
    timers: d.timers,
    onStats: (s) => {
      stats = s;
      report();
    },
  });

  function state(): RemoteState {
    return {
      link,
      host,
      since: Math.max(0, d.timers.now() - lastSignal),
      queued: stats.queued,
      dropped: stats.dropped,
    };
  }
  const report = () => d.onState(state());

  function setLink(next: RemoteState['link']): void {
    if (link === next) return;
    link = next;
    report();
  }

  // --- the stream ---------------------------------------------------------

  function connect(): void {
    if (!running) return;
    // The LAST WRITTEN id, not the last received — see `handle`. A command that arrived while the
    // port was detached is therefore replayed rather than skipped, and the same stored value
    // serves both replay and de-duplication because it means one thing: the newest command this
    // page has actually put on the board.
    const url = eventsUrl(d.base, d.store.get(seenKey));
    lastSignal = d.timers.now(); // a fresh stream gets a full silence window before it is judged
    setLink('connecting');
    stream = d.open(url, { onEvent, onError });
    report(); // unconditional: the banner must appear on the first connect, when link is unchanged
  }

  function onEvent(type: string, id: string, data: string): void {
    // ANY event of ANY type refreshes the watchdog, and `ka` is the one that normally does it on
    // an idle stream. Types this page does not know are still proof of life — which is what lets
    // it survive a server older or newer than itself (02 §5).
    lastSignal = d.timers.now();
    attempt = 0;
    setLink('linked');
    if (type !== 'cmd') {
      report(); // the `since` read-out moved even though the link state did not
      return;
    }
    chain = chain.then(() => handle(id, data)).catch(() => {});
  }

  /**
   * `onerror` is NOT the recovery trigger. Per the SSE specification a non-200 response, or a 200
   * that is not `text/event-stream`, closes the stream permanently with no retry — and the browser
   * also fires `onerror` for its own perfectly ordinary reconnects. It cannot tell those apart, so
   * it only updates the banner. Silence is what triggers recovery (08 §6.4, REQ-REM-7).
   */
  function onError(): void {
    if (!running) return;
    setLink('connecting');
  }

  // --- de-duplication (08 §6.1, 02 §3.3) ----------------------------------

  async function handle(id: string, line: string): Promise<void> {
    const parsed = parseId(id);

    if (parsed && seen && parsed.runtag === seen.runtag && parsed.n <= seen.n) {
      // Already on the board — a replay after a reconnect. Do not write it twice, but DO answer:
      // the laptop is blocked waiting for this id, and silence there costs a whole matrix point.
      outbox.note(`sent ${id}`);
      return;
    }
    if (parsed && (!seen || parsed.runtag !== seen.runtag)) {
      // A new runtag can only mean the server restarted. Adopt it and treat everything as new —
      // but replay nothing at the board, because the board's state is whatever the last session
      // left it as, and re-running an afternoon of commands into it is the worst possible guess.
      seen = { runtag: parsed.runtag, n: 0 };
    }

    // An unparsable id is the laptop's bug, not a reason to drop a command: the board is the
    // authority and will answer for itself. It simply never advances the de-dup watermark.
    let outcome: RemoteOutcome;
    try {
      outcome = await d.deliver(line);
    } catch {
      // 08 §4.3 makes the answer unconditional — every remote command gets exactly one note. An
      // unexpected throw used to be swallowed by the chain's catch, and silence at the laptop is
      // indistinguishable from a dead link, so it would burn the full 30 s wait on every command.
      // `write` is the honest reason: nothing here knows whether the bytes left, and `fail` errs
      // toward "assume it did not happen", which is the safe direction. (A `sent` note is
      // explicitly not a confirmation anyway — only the board's own `ok` on /out is.)
      outbox.note(`fail ${id} write`);
      return;
    }
    if (outcome.kind === 'fail') {
      outbox.note(`fail ${id} ${outcome.reason}`);
      return;
    }
    if (parsed) {
      seen = { runtag: parsed.runtag, n: parsed.n }; // only after a `sent`, never on receipt
      try {
        d.store.set(seenKey, id);
      } catch {
        // Storage disabled or full. De-duplication degrades to in-memory, which is correct for
        // this tab's lifetime; what must not happen is losing the note under it.
      }
    }
    outbox.note(outcome.changed === undefined ? `sent ${id}` : `sent ${id} ${outcome.changed}`);
  }

  // --- the watchdog (08 §6.4, 02 §4.3) ------------------------------------

  function check(): void {
    if (!running) return;
    if (connectHandle !== null) {
      // A reconnect is already scheduled. Without this the watchdog keeps firing every tick
      // against a stream it has already torn down, stacking a fresh reconnect — and bumping the
      // backoff attempt — once per second until the delayed connect finally lands.
      report();
      return;
    }
    const idle = d.timers.now() - lastSignal;
    if (idle >= SILENCE_MS) {
      // close() then construct a NEW EventSource. Not a reload — that costs the WebUSB grant and
      // cannot be asked of a stranger — and not waiting for the browser, which will not retry
      // after a permanent close. Nothing is sent to the board here: an unconditional `stop` would
      // disarm a legitimate `tof` mid-run, and that failure is bit-identical to a genuine
      // below-floor result (06 §12.14 gotcha 4).
      stream?.close();
      stream = null;
      reconnect();
      return;
    }
    if (link === 'linked' && idle >= QUIET_MS) setLink('silent');
    report(); // `since` advances even when nothing else does
  }

  function schedule(): void {
    if (!running || tickHandle !== null) return;
    tickHandle = d.timers.setTimeout(() => {
      tickHandle = null;
      // try/finally, not try/catch: the re-schedule MUST happen or the watchdog stops for the
      // rest of the session and REQ-REM-7 is gone with no symptom until an operator notices a
      // link that never came back. The error still propagates to the browser's handler, because
      // swallowing it here would hide a real fault and there is nothing else to do with it.
      try {
        check();
      } finally {
        schedule();
      }
    }, TICK_MS);
  }

  function reconnect(): void {
    const ms = RECONNECT_MS[Math.min(attempt, RECONNECT_MS.length - 1)] ?? 30_000;
    attempt++;
    setLink('connecting');
    if (ms === 0) {
      connect();
      return;
    }
    connectHandle = d.timers.setTimeout(() => {
      connectHandle = null;
      connect();
    }, ms);
  }

  return {
    start(): void {
      if (running) return;
      running = true;
      const stored = d.store.get(seenKey);
      seen = stored ? parseId(stored) : null;
      try {
        connect();
      } finally {
        schedule(); // a first connect that throws must still leave a watchdog behind it
      }
    },
    stop(): void {
      running = false;
      stream?.close();
      stream = null;
      for (const h of [tickHandle, connectHandle]) if (h !== null) d.timers.clearTimeout(h);
      tickHandle = null;
      connectHandle = null;
      outbox.stop();
      link = 'stopped';
      report();
    },
    poke(): void {
      // A hidden tab freezes setTimeout, so on return the watchdog's own timer may be minutes
      // stale. Check the age now rather than waiting for a tick that did not run.
      if (!running) return;
      try {
        check();
      } finally {
        schedule();
      }
    },
    upstream: outbox,
  };
}
